import {
  formatBytes,
  readCsvFileStream,
  readExcelFile,
  readWorkSheet,
  readSheet,
  convertValuesToString
} from '../../DataMapper/extra/helpers'
export { formatBytes }
import { nanoid } from 'nanoid'
import localforage from 'localforage'
import { produce } from 'immer'
import ExcelJS from 'exceljs'
import { formatExcelDateCellDisplay } from '../../DataMapper/extra/excelCellFormat'
import { workerCode } from './worker'
import {
  MAX_FILE_SIZE,
  CHUNK_SIZE_THRESHOLD,
  CHUNK_SIZE_BYTES,
  CHUNK_SIZE_LARGE,
  formatFileSizeLimit,
  DATA_CATEGORY
} from './constants'
import { FILE_STATE, FILE_TYPE } from './types.js'
import './types.js'

/**
 * S3アップロードパスを生成する
 *
 * @param {string} taskKey
 * @param {string} fileKey
 * @returns {string}
 */
export const getUploadS3Path = (taskKey, fileKey) => `generic-mapping/upload/${taskKey}/${fileKey}`

/**
 * 配列の浅い等価比較
 *
 * @param {any[]} a
 * @param {any[]} b
 * @returns {boolean}
 */
export const shallowEqualArrays = (a, b) => {
  if (!Array.isArray(a) || !Array.isArray(b)) return a === b
  if (a.length !== b.length) return false
  return a.every((v, i) => v === b[i])
}

/**
 * 2つのMap（ユニーク値）をマージする
 *
 * @param {Map<string, Set<string>>} target
 * @param {Map<string, Set<string>>} source
 * @returns {Map<string, Set<string>>}
 */
export const mergeUniqueValuesMap = (target, source) => {
  for (const [key, valueSet] of source.entries()) {
    if (!target.has(key)) {
      target.set(key, new Set())
    }
    for (const value of valueSet) {
      target.get(key).add(value)
    }
  }
  return target
}

/**
 * FileKeyの生成（nanoidを使用）
 *
 * @returns {string}
 */
export const generateFileKey = () => nanoid()

/** ベースのlocalForageインスタンス（メタデータ管理用） */
export const baseLocalForage = localforage.createInstance({
  name: 'file_management_meta',
  driver: [localforage.INDEXEDDB, localforage.LOCALSTORAGE]
})

/**
 * ファイルごとのlocalForageインスタンス（ストア）を作成 fileKeyとtaskKeyを使用し、ストア名が衝突しないようにする
 *
 * @param {string} fileKey
 * @param {string} taskKey
 * @returns {import('localforage')}
 */
export const createFileStore = (fileKey, taskKey) => {
  return localforage.createInstance({
    name: `upload_file_${taskKey}_${fileKey}`,
    driver: [localforage.INDEXEDDB]
  })
}

/**
 * ファイルが削除保留状態かどうかを判定
 *
 * @param {string} fileState
 * @returns {boolean}
 */
export const isFileDeleted = (fileState) => fileState === FILE_STATE.PENDING_DELETION

/**
 * ファイルストアのキー定義 ストア構造: upload_file_{fileKey} = { reference: file_object, headers: [], row_chunk_{index}: [...],
 * chunk_count: number }
 */
export const FILE_STORE_KEYS = {
  REFERENCE: 'reference', // Fileオブジェクトの参照
  HEADERS: 'headers', // ヘッダー配列
  CHUNK_COUNT: 'chunk_count', // チャンク数
  UNIQUE_VALUES_CALCULATED: 'unique_values_calculated' // ユニーク値計算済みフラグ
}

/**
 * ユニーク値キーの生成
 *
 * @param {string} columnName
 * @returns {string}
 */
export const getUniqueValuesKey = (columnName) => `unique_values_${columnName}`

/**
 * チャンクキーの生成
 *
 * @param {number} index
 * @returns {string}
 */
export const getChunkKey = (index) => `row_chunk_${index}`

/**
 * ファイルストア用のチャンク化バッファリング チャンクサイズ: バイトベースで計算（1MB/チャンク）
 *
 * @param {import('localforage')} fileStore
 * @param {number} fileSize
 * @param {number} [estimatedRowCount] - 推定行数（オプション）
 * @returns {{ add: function; finish: function }}
 */
export function createFileStoreBufferedIO(fileStore, fileSize, estimatedRowCount) {
  // 推定行サイズからチャンク行数を算出（最低10行）
  const estimatedRowSize = fileSize / (estimatedRowCount || 1000)
  const chunkSize = Math.max(10, Math.floor(CHUNK_SIZE_BYTES / (estimatedRowSize || 1000)))

  let buffer = []
  let chunkIndex = 0

  const flush = async () => {
    if (buffer.length === 0) return

    // チャンク化された行を保存
    const chunkKey = getChunkKey(chunkIndex)
    // rows(string[])[] の形で保存する（flatにすると行構造が壊れて読み込み・CSV生成時に破綻する）
    await fileStore.setItem(chunkKey, buffer)

    // チャンク数を更新
    await fileStore.setItem(FILE_STORE_KEYS.CHUNK_COUNT, chunkIndex + 1)

    buffer = []
    chunkIndex++
  }

  const add = async (row) => {
    buffer.push(row)
    if (buffer.length >= chunkSize) {
      await flush()
    }
  }

  const finish = async () => {
    await flush()
  }

  return { add, finish }
}

/**
 * ファイルストアからチャンク化された行を読み込む
 *
 * @param {import('localforage')} fileStore
 * @returns {Promise<string[][]>}
 */
export async function loadChunkedRowsFromFileStore(fileStore) {
  const chunkCount = (await fileStore.getItem(FILE_STORE_KEYS.CHUNK_COUNT)) || 0
  if (chunkCount === 0) return []

  const chunks = []
  for (let i = 0; i < chunkCount; i++) {
    const chunkKey = getChunkKey(i)
    const chunk = await fileStore.getItem(chunkKey)
    if (chunk) {
      chunks.push(...chunk)
    }
  }

  return chunks
}

/**
 * ファイルストアから行数を計算（ヘッダー行を含む）
 *
 * @param {import('localforage')} fileStore
 * @returns {Promise<number>}
 */
export async function calculateRowCountFromFileStore(fileStore) {
  const chunkCount = (await fileStore.getItem(FILE_STORE_KEYS.CHUNK_COUNT)) || 0
  if (chunkCount === 0) {
    // ヘッダーのみの場合は1行
    const headers = (await fileStore.getItem(FILE_STORE_KEYS.HEADERS)) || []
    return headers.length > 0 ? 1 : 0
  }

  let totalRows = 1 // ヘッダー行を含む
  for (let i = 0; i < chunkCount; i++) {
    const chunkKey = getChunkKey(i)
    const chunk = await fileStore.getItem(chunkKey)
    if (chunk && Array.isArray(chunk)) {
      totalRows += chunk.length
    }
  }

  return totalRows
}

/**
 * ファイルストアからデータ行数を計算（ヘッダー行を除く）
 *
 * 画面表示用の行数。レポートデータ更新画面の表示（登録時の row_count）に 合わせ、ヘッダー行を除いたデータ行数を返す。
 *
 * @param {import('localforage')} fileStore
 * @returns {Promise<number>}
 */
export async function calculateDataRowCountFromFileStore(fileStore) {
  const totalRows = await calculateRowCountFromFileStore(fileStore)
  return Math.max(0, totalRows - 1)
}

/**
 * ファイルストアを完全に削除（データベースごと削除）
 *
 * @param {string} fileKey
 * @param {string} taskKey
 * @returns {Promise<void>}
 */
export const deleteFileStore = async (fileKey, taskKey) => {
  // localforage.dropInstance()を使用してデータベースを完全に削除
  await localforage.dropInstance({
    name: `upload_file_${taskKey}_${fileKey}`
  })
}

/** バリデーション関数 */

/**
 * ファイルが CSV かどうかを拡張子で判定する（MIME type 非依存） Firefox on Windows では CSV が application/vnd.ms-excel として返されるため拡張子で判定する
 *
 * @param {File} file
 * @returns {boolean}
 */
export const isCsvFile = (file) => file.name.toLowerCase().endsWith('.csv')

/**
 * ファイルが Excel かどうかを拡張子で判定する（MIME type 非依存）
 *
 * @param {File} file
 * @returns {boolean}
 */
export const isExcelFile = (file) => /\.(xlsx|xls)$/i.test(file.name)

/**
 * ファイル形式のバリデーション
 *
 * @param {File} file
 * @returns {boolean}
 */
export const validateFileFormat = (file) => isCsvFile(file) || isExcelFile(file)

/**
 * 既存ファイル一覧を状態に反映し、必要に応じてS3からダウンロードしてIndexedDBに保存する。
 *
 * @param {any} existingFilesRes
 * @param {any} dataCreateTaskRes
 * @param {string} corporationNumber
 * @param {any} actions
 * @param {string} userSub
 */
export const _processExistingFilesIntoState = (
  existingFilesRes,
  dataCreateTaskRes,
  corporationNumber,
  actions,
  userSub
) => {
  if (!existingFilesRes.body || existingFilesRes.body.length === 0) {
    actions.setSavedUploadState({})
    actions.resetChangedFlags()
    return
  }

  const initialFileList = {}
  const taskKey = dataCreateTaskRes.body.taskKey

  // 1. まず全ファイルを同期的に state に追加して一覧を表示可能にする
  for (const fileData of existingFilesRes.body) {
    const file = normalizeExistingFileFromApi(fileData)
    if (!file?.fileKey) continue
    actions.addFile(file.fileKey, file)
    initialFileList[file.fileKey] = { ...file }

    // pending-virus-check は変換済みファイルが企業バケットに未出力のためダウンロード対象外
    const shouldDownload = file.fileState === FILE_STATE.UPLOADED || file.fileState === FILE_STATE.VALIDATION_ERROR

    if (shouldDownload) {
      // 2. ダウンロード処理を開始（await しない）。
      //    実行中のみ「データ表示」スピナーを出すため isDownloading を立て、完了/失敗時に必ず倒す。
      //    （元ファイル削除済みでダウンロードが失敗するケースでもスピナーが回り続けないようにする）
      actions.initFileData(file.fileKey, { isDownloading: true })
      downloadAndProcessExistingFile(file, taskKey, corporationNumber)
        .then(({ headers, isStoredLocally }) => {
          // 初期化時のデータ補完（isChangedを立てず、ベースラインも更新）
          actions.initFileData(file.fileKey, { headers, isStoredLocally, isDownloading: false })
        })
        .catch((error) => {
          // ダウンロード失敗は一時エラーの可能性があるため、ステータスは書き換えず未保持のまま扱う
          console.error(error?.stack ?? error)
          actions.initFileData(file.fileKey, { isStoredLocally: false, isDownloading: false })
        })
    }
  }

  // 3. 同期的にベースラインを設定し、初期化による変更フラグをクリア
  actions.setSavedUploadState(initialFileList)
  actions.resetChangedFlags()
}

/**
 * ファイルサイズのバリデーション（既存ファイルを含む合計サイズをチェック）
 *
 * @param {File} newFile
 * @param {Record<string, import('./types').FileState>} existingFiles
 * @param {number} maxSize
 * @returns {boolean}
 */
export const validateFileSize = (newFile, existingFiles = {}, maxSize = MAX_FILE_SIZE) => {
  const existingTotalSize = Object.values(existingFiles)
    .filter((f) => !isFileDeleted(f.fileState))
    .reduce((sum, f) => sum + (f.fileSize || 0), 0)

  return existingTotalSize + (newFile.size || 0) <= maxSize
}

/**
 * ファイル名のバリデーション（OSファイル名制限）
 *
 * @param {string} fileName
 * @returns {{ valid: boolean; error?: string }}
 */
/** 仕様10: ファイル名チェック時のエラーメッセージ */
const FILE_NAME_VALIDATION_MESSAGE = 'OSのファイル名制限に従う（Windowsは255文字、禁止文字 / \\ : * ? " < > |）'

export const validateFileName = (fileName) => {
  if (!fileName || fileName.trim() === '') {
    return { valid: false, error: FILE_NAME_VALIDATION_MESSAGE }
  }

  if (fileName.length > 255) {
    return { valid: false, error: FILE_NAME_VALIDATION_MESSAGE }
  }

  const invalidChars = /[\/\\:\*\?"<>\|]/
  if (invalidChars.test(fileName)) {
    return { valid: false, error: FILE_NAME_VALIDATION_MESSAGE }
  }

  return { valid: true }
}

/**
 * ヘッダーリストの一致チェック
 *
 * @param {string[]} headers
 * @param {string[][]} dataRows
 * @returns {boolean}
 */
export const validateHeaderConsistency = (headers, dataRows) => {
  if (!headers || headers.length === 0) return false
  if (!dataRows || dataRows.length === 0) return true // データ行がない場合はOK

  // すべてのデータ行が同じ列数を持つかチェック
  const expectedColumnCount = headers.length
  return dataRows.every((row) => row.length === expectedColumnCount)
}

/**
 * シート名の一致チェック
 *
 * @param {string} sheetName
 * @param {string[]} sheetList
 * @param {boolean} isExcel
 * @returns {boolean}
 */
export const validateSheetName = (sheetName, sheetList, isExcel) => {
  if (!isExcel) {
    // CSVファイルの場合は"0"であることを確認
    return sheetName === '0'
  }

  // Excelファイルの場合はsheetListに存在することを確認
  return sheetList.includes(sheetName)
}

/**
 * 空ファイルチェック（Excel）
 *
 * @param {string[]} sheetList
 * @param {string[][]} headers
 * @returns {boolean}
 */
export const validateEmptyFile = (sheetList, headers) => {
  // シート数が0、またはヘッダー行が存在しない場合はエラー
  if (sheetList.length === 0) return false
  if (!headers || headers.length === 0) return false
  return true
}

/** Blob/データ変換ヘルパー */

/**
 * Blobまたは文字列をテキストとして読み取る
 *
 * @param {Blob | string | null} blob
 * @returns {Promise<string>}
 */
export const readBlobText = async (blob) => {
  if (blob == null) return ''
  if (typeof blob === 'string') return blob
  if (typeof blob.text === 'function') return await blob.text()
  if (typeof blob.arrayBuffer === 'function') {
    const buffer = await blob.arrayBuffer()
    return new TextDecoder('utf-8').decode(buffer)
  }
  return ''
}

/**
 * ヘッダーと行データからCSV用Blobを生成する
 *
 * @param {string[]} headers
 * @param {string[][]} rows
 * @returns {Blob}
 */
export const buildCsvBlobFromStoredFile = (headers, rows) => {
  const csvRows = [headers, ...(rows || [])]
  const csvText = csvRows.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n')
  return new Blob([csvText], { type: 'text/csv' })
}

/**
 * API取得の既存ファイル1件を状態用オブジェクトに正規化する
 *
 * @param {any} fileData
 * @returns {import('./types').FileState & { updateUserName?: string; updatedAt?: string }}
 */
export const normalizeExistingFileFromApi = (fileData) => {
  if (fileData == null) return null
  const fileKey = fileData.fileKey ?? fileData.file_key ?? ''
  return {
    ...fileData,
    fileKey,
    fileClassification:
      fileData.fileClassification ||
      fileData.fileClassificationKey ||
      fileData.mappingFileType ||
      fileData.MappingFileType ||
      '',
    headers: Array.isArray(fileData.headers) ? fileData.headers : [],
    dataCategory: _string(fileData.dataCategory ?? fileData.data_category ?? fileData.DataCategory),
    updateUserName: fileData.updateUserName || '',
    updatedAt: fileData.updatedAt || '',
    isStoredLocally: false,
    isUploadedLocally: false,
    isBaseFile: Boolean(fileData.isBaseFile || fileData.is_base_file || fileData.IsBaseFile),
    isChanged: false
  }
}

/**
 * パース済みデータとFileから新規アップロード用のFileStateオブジェクトを生成する
 *
 * @param {{ headers?: string[]; sheetName?: string; sheetList?: string[] }} parsedData
 * @param {File} file
 * @param {string} fileKey
 * @param {string} userName
 * @returns {import('./types').FileState}
 */
/** 仕様 9.2: キー項目の自動検出は実装しない。ユーザーが手動で選択する。 */
export const buildFileObjectFromParsedData = (parsedData, file, fileKey, userName) => {
  const sheetName = parsedData.sheetName || (isCsvFile(file) ? '0' : '')
  const sheetList = parsedData.sheetList || []
  return {
    fileKey,
    fileName: file.name,
    fileSize: file.size,
    fileType: isCsvFile(file) ? FILE_TYPE.CSV : FILE_TYPE.EXCEL,
    fileState: FILE_STATE.PENDING_VIRUS_CHECK,
    headers: parsedData.headers || [],
    fileClassification: '',
    primaryKeyColumn: '',
    dataCategory: '',
    idConversionTable: '',
    exclusionConditions: [],
    registeredExclusionConditions: [],
    hashColumns: [],
    registeredHashColumns: [],
    sheetName,
    sheetList,
    uploadedUsername: userName,
    uploadedDateTime: new Date().toISOString(),
    isStoredLocally: true,
    isUploadedLocally: true,
    isBaseFile: false,
    isChanged: true,
    updateUserName: '',
    updatedAt: ''
  }
}

/**
 * ファイル差し替え時に更新用FileStateを生成する
 *
 * 詳細設定（ID変換表・除外条件・ハッシュ化列）はクリアせず保持する。 ただし未登録ファイルの差し替えはヘッダーが変わり得るため、シート変更
 * （calculateNextFileStateAfterSheetChange）と同様に新ヘッダーに存在する列のみ保持する。
 * 登録済みロック用ミラー（registered系）は、差し替え後は新データを全面再処理するためクリアして全編集可能に戻す。 除外条件の選択値の絞り込み（変更後ファイルに存在する値のみ保持）は、ユニーク値の参照が必要なため呼び出し元で
 * filterExclusionValuesForNewFile により行う。
 *
 * @param {import('./types').FileState} file
 * @param {File} newFile
 * @param {{ headers?: string[]; sheetName?: string; sheetList?: string[] }} parsedData
 * @returns {import('./types').FileState}
 */
export const buildUpdatedFileAfterChange = (file, newFile, parsedData) => {
  const sheetName = parsedData.sheetName || (isCsvFile(newFile) ? '0' : '')
  const sheetList = parsedData.sheetList || []
  const fileType = isCsvFile(newFile) ? FILE_TYPE.CSV : FILE_TYPE.EXCEL
  const newHeaders = parsedData.headers || []
  return {
    ...file,
    fileName: newFile.name,
    fileSize: newFile.size,
    fileType,
    headers: newHeaders,
    exclusionConditions: (file.exclusionConditions || []).filter((cond) => newHeaders.includes(cond.column)),
    registeredExclusionConditions: [],
    hashColumns: (file.hashColumns || []).filter((col) => newHeaders.includes(col)),
    registeredHashColumns: [],
    sheetName,
    sheetList,
    fileState: FILE_STATE.PENDING_VIRUS_CHECK,
    isUploadedLocally: true,
    isStoredLocally: true,
    isChanged: true,
    // fileKey・列構成が同じままファイル内容だけ変わるため、除外値候補などの再取得トリガーとして版数を上げる
    contentRevision: (file.contentRevision ?? 0) + 1
  }
}

/**
 * 除外条件の選択値を変更後ファイルのユニーク値に合わせて絞り込む。 変更前のファイルにしかない値は落とし、値が0件になった条件も列選択は残す （登録時の既存バリデーション「除外する値を1件以上選択してください」で検出させる）。
 *
 * @param {{ column: string; values: string[] }[]} [exclusionConditions]
 * @param {function(string): Promise<Set<string>>} getUniqueValuesForColumn - 列名から変更後ファイルのユニーク値を取得する
 * @returns {Promise<{ column: string; values: string[] }[]>}
 */
export const filterExclusionValuesForNewFile = async (exclusionConditions, getUniqueValuesForColumn) => {
  const result = []
  for (const cond of exclusionConditions || []) {
    if (!cond.column) {
      result.push({ ...cond })
      continue
    }
    const uniqueValues = await getUniqueValuesForColumn(cond.column)
    result.push({ ...cond, values: (cond.values || []).filter((value) => uniqueValues.has(value)) })
  }
  return result
}

/**
 * 同一ファイル種別間のヘッダー整合性のバリデーション
 *
 * @param {Record<string, import('./types').FileState>} fileList
 * @param {string} fileClassification
 * @returns {boolean}
 */
export const validateHeaderConsistencyAcrossFiles = (fileList, fileClassification) => {
  const filesWithSameType = Object.values(fileList).filter((file) => file.fileClassification === fileClassification)

  if (filesWithSameType.length <= 1) return true

  const firstHeaders = filesWithSameType[0].headers
  return filesWithSameType.every((file) => {
    if (file.headers.length !== firstHeaders.length) return false
    return file.headers.every((header, index) => header === firstHeaders[index])
  })
}

/** 除外条件関連関数 */

/**
 * ワーカーを使用してチャンク内のユニーク値を計算する。 返るのはこのチャンク分のみ。累積は呼び出し側で mergeUniqueValuesMap
 * により合算する（累積マップ全体をワーカーと往復させると大容量ファイルで転送コストが増大し続けるため、チャンク分だけを送受信する）。
 *
 * @param {string[][]} chunk
 * @param {string[]} headers
 * @param {number} timeoutMs - タイムアウト時間（ミリ秒）
 * @returns {Promise<Map<string, Set<string>>>} このチャンク分のユニーク値
 */
export const calculateUniqueValuesPerColumn = async (chunk, headers, timeoutMs = 30000) => {
  if (!chunk || chunk.length === 0) return new Map()

  return new Promise((resolve, reject) => {
    const blob = new Blob([workerCode], { type: 'text/javascript' })
    const url = URL.createObjectURL(blob)
    const worker = new Worker(url)

    const timeoutId = setTimeout(() => {
      worker.terminate()
      URL.revokeObjectURL(url)
      reject(new Error('ユニーク値計算処理がタイムアウトしました（30秒）'))
    }, timeoutMs)

    worker.onmessage = (e) => {
      clearTimeout(timeoutId)
      worker.terminate()
      URL.revokeObjectURL(url)

      if (e.data.error) {
        reject(new Error(e.data.error))
        return
      }

      if (!e.data.success) {
        reject(new Error('Worker処理が失敗しました'))
        return
      }

      // ワーカーから返されたオブジェクトをMapに変換
      const updatedMap = new Map()
      for (const [key, valueArray] of Object.entries(e.data.uniqueValuesMap)) {
        updatedMap.set(key, new Set(valueArray))
      }

      resolve(updatedMap)
    }

    worker.onerror = (e) => {
      clearTimeout(timeoutId)
      worker.terminate()
      URL.revokeObjectURL(url)
      console.error('Worker error:', e)
      console.error(e?.error?.stack ?? e?.stack ?? e)
      reject(e)
    }

    worker.postMessage({
      chunk,
      headers
    })
  })
}

/**
 * ユニーク値をIndexedDBに保存
 *
 * @param {import('localforage')} fileStore
 * @param {Map<string, Set<string>>} uniqueValuesMap
 * @returns {Promise<void>}
 */
export const saveUniqueValuesToIndexDb = async (fileStore, uniqueValuesMap) => {
  try {
    // 各カラムのユニーク値を保存
    for (const [columnName, uniqueValues] of uniqueValuesMap.entries()) {
      const key = getUniqueValuesKey(columnName)
      // SetをArrayに変換して保存
      await fileStore.setItem(key, Array.from(uniqueValues))
    }
    // 計算済みフラグを設定
    await fileStore.setItem(FILE_STORE_KEYS.UNIQUE_VALUES_CALCULATED, true)
  } catch (error) {
    console.error('Failed to save unique values to IndexedDB:', error)
    console.error(error?.stack ?? error)
    throw error
  }
}

/**
 * 除外条件のユニーク値を取得
 *
 * @param {string} fileKey
 * @param {string} columnName
 * @param {import('localforage')} fileStore
 * @returns {Promise<Set<string>>}
 */
export const getUniqueValuesForExclusion = async (fileKey, columnName, fileStore) => {
  try {
    // まずIndexedDBから読み込む
    const uniqueValuesKey = getUniqueValuesKey(columnName)
    const cachedValues = await fileStore.getItem(uniqueValuesKey)

    if (cachedValues && Array.isArray(cachedValues)) {
      // IndexedDBから読み込めた場合はそれを返す
      return new Set(cachedValues)
    }

    // IndexedDBに存在しない場合、チャンクごとに読み込んで計算（フォールバック）
    const headers = (await fileStore.getItem(FILE_STORE_KEYS.HEADERS)) || []
    const columnIndex = headers.indexOf(columnName)
    if (columnIndex === -1) return new Set()

    const uniqueValues = new Set()
    const chunkCount = (await fileStore.getItem(FILE_STORE_KEYS.CHUNK_COUNT)) || 0

    // チャンクごとに読み込んでユニーク値を計算
    for (let i = 0; i < chunkCount; i++) {
      const chunkKey = getChunkKey(i)
      const chunk = await fileStore.getItem(chunkKey)
      if (chunk && Array.isArray(chunk)) {
        chunk.forEach((row) => {
          const value = convertValuesToString(row[columnIndex] || '')
          if (value !== '') {
            uniqueValues.add(value)
          } else {
            uniqueValues.add('')
          }
        })
      }
    }

    // 計算したユニーク値をIndexedDBに保存（次回の高速化のため）
    if (uniqueValues.size > 0) {
      await fileStore.setItem(uniqueValuesKey, Array.from(uniqueValues))
      await fileStore.setItem(FILE_STORE_KEYS.UNIQUE_VALUES_CALCULATED, true)
    }

    return uniqueValues
  } catch (error) {
    console.error('Failed to get unique values for exclusion:', error)
    return new Set()
  }
}

/**
 * 除外条件の値をフィルタリング
 *
 * @param {Set<string>} uniqueValues
 * @param {string} searchKeyword
 * @returns {string[]}
 */
export const filterExclusionValues = (uniqueValues, searchKeyword) => {
  if (!searchKeyword) return Array.from(uniqueValues)
  return Array.from(uniqueValues).filter((value) => String(value).toLowerCase().includes(searchKeyword.toLowerCase()))
}

/**
 * 除外条件の値リストを組み立てる（空欄がある場合のみ先頭に '' を配置）
 *
 * @param {string[]} values
 * @returns {string[]}
 */
export const buildExclusionValueList = (values) => {
  if (!values.includes('')) return values
  return ['', ...values.filter((v) => v !== '')]
}

/**
 * 除外条件の対象カラムがハッシュ化対象列として選択されているか判定する
 *
 * @param {string} columnName
 * @param {string[]} [hashColumns]
 * @param {string[]} [registeredHashColumns]
 * @returns {boolean}
 */
export const isHashTargetColumn = (columnName, hashColumns = [], registeredHashColumns = []) => {
  if (!columnName) return false
  return [...(hashColumns || []), ...(registeredHashColumns || [])].includes(columnName)
}

/**
 * 除外条件の対象カラムが登録済みハッシュ化対象列か判定する（サーバ上でハッシュ化済み）
 *
 * @param {string} columnName
 * @param {string[]} [registeredHashColumns]
 * @returns {boolean}
 */
export const isRegisteredHashTargetColumn = (columnName, registeredHashColumns = []) => {
  if (!columnName) return false
  return (registeredHashColumns || []).includes(columnName)
}

/**
 * ハッシュ化済みカラムの除外条件表示用の値リストを組み立てる。 登録済み・選択済みのハッシュ化前の値のみ表示し、ハッシュ化後のユニーク値は含めない。
 *
 * @param {string[]} registeredValues
 * @param {string[]} selectedValues
 * @param {string} searchKeyword
 * @returns {string[]}
 */
export const buildHashedColumnExclusionDisplayValues = (registeredValues, selectedValues, searchKeyword) => {
  const preserved = [...new Set([...(registeredValues || []), ...(selectedValues || [])])]
  const filtered = filterExclusionValues(new Set(preserved), searchKeyword)
  return buildExclusionValueList(filtered)
}

/**
 * 除外条件の対象カラム選択肢を組み立てる（他条件で選択済みのカラムは除外）
 *
 * @param {string[]} headers
 * @param {string} primaryKeyColumn
 * @param {import('./types').ExcludedRowCondition[]} exclusionConditions
 * @param {number} conditionIndex
 * @param {string} currentColumn
 * @returns {{ value: string; label: string }[]}
 */
export const buildExclusionColumnOptions = (
  headers,
  primaryKeyColumn,
  exclusionConditions,
  conditionIndex,
  currentColumn
) => {
  const columnsUsedByOtherConditions = new Set(
    (exclusionConditions || [])
      .filter((_, idx) => idx !== conditionIndex)
      .map((cond) => cond.column)
      .filter(Boolean)
  )
  const selectableHeaders = (headers || []).filter((header) => {
    if (header === primaryKeyColumn) {
      return header === currentColumn
    }
    if (columnsUsedByOtherConditions.has(header)) {
      return header === currentColumn
    }
    return true
  })
  return [
    { value: '', label: '-- 対象カラムを選択 --' },
    ...selectableHeaders.map((header) => ({ value: header, label: header }))
  ]
}

/**
 * ストリーミング処理の共通コア
 *
 * @param {AsyncIterable<string[][]>} gen
 * @param {import('localforage')} fileStore
 * @param {Map<string, Set<string>>} uniqueValuesMap
 * @param {object} bufferedIO
 * @param {object} options
 * @returns {Promise<{ headers: string[]; recordCount: number }>}
 */
async function _processStreamingCore(gen, fileStore, uniqueValuesMap, bufferedIO, options = {}) {
  const { isCsv, providedHeaders } = options
  let headers = providedHeaders || []
  let recordCount = 0
  let firstChunk = true

  for await (const chunk of gen) {
    let dataChunk = chunk
    if (firstChunk && isCsv) {
      headers = chunk[0] || []
      await fileStore.setItem(FILE_STORE_KEYS.HEADERS, headers)
      dataChunk = chunk.slice(1)
      firstChunk = false
    }

    for (const row of dataChunk) {
      await bufferedIO.add(row)
      recordCount++
    }

    const chunkUniqueValuesMap = await calculateUniqueValuesPerColumn(dataChunk, headers)
    mergeUniqueValuesMap(uniqueValuesMap, chunkUniqueValuesMap)
  }

  return { headers, recordCount }
}

/**
 * CSVファイルのストリーミング処理
 *
 * @param {File} file
 * @param {import('localforage')} fileStore
 * @param {function(number): void} onProgress
 * @param {Map<string, Set<string>>} uniqueValuesMap
 * @param {object} bufferedIO
 * @returns {Promise<{ headers: string[]; recordCount: number; sheetName: string; sheetList: string[] }>}
 */
async function _processCsvStreaming(file, fileStore, onProgress, uniqueValuesMap, bufferedIO) {
  const gen = readCsvFileStream(file, 1000, onProgress)
  const { headers, recordCount } = await _processStreamingCore(gen, fileStore, uniqueValuesMap, bufferedIO, {
    isCsv: true
  })

  return {
    headers,
    recordCount,
    sheetName: '0',
    sheetList: []
  }
}

/**
 * Excelファイルのストリーミング処理
 *
 * @param {File} file
 * @param {import('localforage')} fileStore
 * @param {function(number): void} onProgress
 * @param {string} sheetName
 * @param {Map<string, Set<string>>} uniqueValuesMap
 * @param {object} bufferedIO
 * @returns {Promise<{ headers: string[]; recordCount: number; sheetName: string; sheetList: string[] }>}
 */
async function _processExcelStreaming(file, fileStore, onProgress, sheetName, uniqueValuesMap, bufferedIO) {
  const workbook = await readExcelFile(file, onProgress)
  const sheetList = []
  workbook.eachSheet((ws) => sheetList.push(ws.name))

  const targetSheetName = sheetName || sheetList[0] || ''
  const worksheet = workbook.getWorksheet(targetSheetName)
  if (!worksheet) throw new Error('シートが見つかりません')

  // ヘッダー取得
  const headers = []
  const headerRow = worksheet.getRow(1)
  for (let i = 1; ; i++) {
    const cell = headerRow.getCell(i)
    const val = cell.type === ExcelJS.ValueType.Formula ? cell.result : cell.value
    if (!val) break
    headers.push(String(val))
  }
  await fileStore.setItem(FILE_STORE_KEYS.HEADERS, headers)

  // レコードをジェネレーター化して共通コアに渡す
  async function* excelRowGenerator() {
    let rowIndex = 2
    let currentChunk = []
    while (true) {
      const row = worksheet.getRow(rowIndex++)
      if (!row.hasValues) break

      const rec = headers.map((_, j) => {
        const cell = row.getCell(j + 1)
        if (cell.type === ExcelJS.ValueType.Formula) return String(cell.result ?? '')
        if (cell.type === ExcelJS.ValueType.Date) return formatExcelDateCellDisplay(cell.value, cell.numFmt)
        return String(cell.value ?? '')
      })

      currentChunk.push(rec)
      if (currentChunk.length >= CHUNK_SIZE_LARGE) {
        yield currentChunk
        currentChunk = []
      }
    }
    if (currentChunk.length > 0) yield currentChunk
  }

  const { recordCount } = await _processStreamingCore(excelRowGenerator(), fileStore, uniqueValuesMap, bufferedIO, {
    providedHeaders: headers
  })

  return {
    headers,
    recordCount,
    sheetName: targetSheetName,
    sheetList
  }
}

/**
 * ストリーミングファイル処理（チャンクごとにIndexedDBに保存、メモリ効率化）
 *
 * @param {File} file
 * @param {import('localforage')} fileStore
 * @param {function(number): void} onProgress
 * @param {string} sheetName - Excelの場合のシート名（オプション）
 * @returns {Promise<{ headers: string[]; recordCount: number; sheetName?: string; sheetList?: string[] }>}
 */
export const processFileStreaming = async (file, fileStore, onProgress, sheetName = '') => {
  const uniqueValuesMap = new Map()

  // ファイルストアをクリア
  await fileStore.clear()

  // バッファリングIOを作成
  const bufferedIO = createFileStoreBufferedIO(fileStore, file.size)

  let result
  if (isCsvFile(file)) {
    result = await _processCsvStreaming(file, fileStore, onProgress, uniqueValuesMap, bufferedIO)
  } else {
    result = await _processExcelStreaming(file, fileStore, onProgress, sheetName, uniqueValuesMap, bufferedIO)
  }

  // バッファをフラッシュ
  await bufferedIO.finish()

  // ユニーク値をIndexedDBに保存
  await saveUniqueValuesToIndexDb(fileStore, uniqueValuesMap)

  return result
}

/**
 * Excelファイルの全シート情報を取得（シートリスト用）
 *
 * @param {File} file
 * @returns {Promise<string[]>}
 */
export const getExcelSheetList = async (file) => {
  try {
    const workbook = await readExcelFile(file, () => {})
    const sheetList = []
    workbook.eachSheet((ws) => {
      sheetList.push(ws.name)
    })
    return sheetList
  } catch (error) {
    console.error('Failed to get Excel sheet list:', error)
    console.error(error?.stack ?? error)
    return []
  }
}

/**
 * 除外条件の等価比較（順序に依存しない）
 *
 * @param {import('./types').ExcludedRowCondition[]} a
 * @param {import('./types').ExcludedRowCondition[]} b
 * @returns {boolean}
 */
/**
 * 除外条件リストのシャローコピー
 *
 * @param {import('./types').ExcludedRowCondition[]} [conditions]
 * @returns {import('./types').ExcludedRowCondition[]}
 */
export const cloneExclusionConditions = (conditions) =>
  (conditions || []).map((c) => ({
    column: c.column ?? '',
    values: [...(c.values || [])]
  }))

/**
 * 登録済み除外条件から対象カラムの values を取得
 *
 * @param {import('./types').ExcludedRowCondition[]} [registered]
 * @param {string} columnName
 * @returns {string[]}
 */
export const getRegisteredExclusionValuesForColumn = (registered, columnName) => {
  if (!columnName) return []
  const found = (registered || []).find((c) => c.column === columnName)
  return found?.values ?? []
}

/**
 * キー項目と競合する除外条件があるか判定する
 *
 * @param {import('./types').FileState} file
 * @returns {boolean}
 */
export const hasExclusionConflictWithPrimaryKey = (file) => {
  const primaryKeyColumn = file.primaryKeyColumn
  if (!primaryKeyColumn) return false
  return (file.exclusionConditions || []).some((cond) => cond.column === primaryKeyColumn)
}

/**
 * キー項目と競合するハッシュ化対象列があるか判定する。 キー項目はハッシュ化対象列に指定できない（キー列はデータ種別が従業員別のときのみ
 * キー変換側でハッシュ化され、会社別・未設定では平文のまま維持されるため、ハッシュ化対象列として登録すると不整合になる）。
 *
 * @param {import('./types').FileState} file
 * @returns {boolean}
 */
export const hasHashColumnConflictWithPrimaryKey = (file) => {
  const primaryKeyColumn = file.primaryKeyColumn
  if (!primaryKeyColumn) return false
  return (file.hashColumns || []).includes(primaryKeyColumn)
}

export const deepEqualExclusionConditions = (a, b) => {
  if (!a || !b) return a === b
  if (!Array.isArray(a) || !Array.isArray(b)) return a === b
  if (a.length !== b.length) return false

  const sortAndStringify = (cond) => {
    const values = Array.isArray(cond.values) ? [...cond.values].sort().join(',') : ''
    return `${cond.column}:${values}`
  }

  const setA = new Set(a.map(sortAndStringify))
  const setB = new Set(b.map(sortAndStringify))

  return setA.size === setB.size && [...setA].every((v) => setB.has(v))
}

/**
 * シート変更後のファイル状態（ヘッダー、キー項目、除外条件）を計算する
 *
 * @param {import('./types').FileState} file - 現在のファイル状態
 * @param {string[]} nextHeaders - 新しいシートのヘッダー
 * @param {string} nextSheetName - 新しいシート名
 * @param {string[]} nextSheetList - シートリスト
 * @returns {Partial<import('./types').FileState>}
 */
export const calculateNextFileStateAfterSheetChange = (file, nextHeaders, nextSheetName, nextSheetList) => {
  const nextPrimaryKeyColumn =
    file.primaryKeyColumn && nextHeaders.includes(file.primaryKeyColumn) ? file.primaryKeyColumn : ''

  const nextExclusionConditions = (file.exclusionConditions || [])
    .map((cond) => (nextHeaders.includes(cond.column) ? cond : null))
    .filter((cond) => cond !== null)

  const nextRegisteredExclusionConditions = (file.registeredExclusionConditions || [])
    .map((cond) => (nextHeaders.includes(cond.column) ? cond : null))
    .filter((cond) => cond !== null)

  const nextHashColumns = (file.hashColumns || []).filter((col) => nextHeaders.includes(col))
  const nextRegisteredHashColumns = (file.registeredHashColumns || []).filter((col) => nextHeaders.includes(col))

  return {
    headers: nextHeaders,
    sheetName: nextSheetName,
    sheetList: nextSheetList,
    primaryKeyColumn: nextPrimaryKeyColumn,
    exclusionConditions: nextExclusionConditions,
    registeredExclusionConditions: nextRegisteredExclusionConditions,
    hashColumns: nextHashColumns,
    registeredHashColumns: nextRegisteredHashColumns,
    isStoredLocally: true,
    isChanged: true
  }
}

/**
 * アップロード/差し替え時の共通処理（IndexedDB保存とメタデータ更新）
 *
 * @param {string} fileKey
 * @param {string} taskKey
 * @param {File} file
 * @param {string} sheetName
 * @returns {Promise<{ parsedData: import('./types').ParsedFileData; isExcel: boolean }>}
 */
export const prepareFileStoreForUpload = async (fileKey, taskKey, file, sheetName = '') => {
  const fileStore = createFileStore(fileKey, taskKey)
  const parsedData = await processFileStreaming(file, fileStore, () => {}, sheetName)

  const isExcel = isExcelFile(file)
  if (isExcel) {
    await baseLocalForage.setItem(`source_file_${fileKey}`, file)
    await fileStore.setItem('source_file', file)
  }

  return { parsedData, isExcel }
}

/**
 * @param {unknown} v
 * @returns {string}
 */
export const _string = (v) => (v == null ? '' : String(v))

/**
 * @param {unknown} v
 * @returns {number}
 */
export const _number = (v) => {
  if (v == null) return 0
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

/** @param {unknown} v @returns {unknown[]} */
export const _toArray = (v) => (Array.isArray(v) ? v : [])

/**
 * @param {unknown} v
 * @returns {string[]}
 */
export const _stringArray = (v) => _toArray(v).map(_string).filter(Boolean)

/**
 * 除外条件の values 用（空文字 "" を保持する）
 *
 * @param {unknown} v
 * @returns {string[]}
 */
export const _exclusionValuesArray = (v) => _toArray(v).map(_string)

/**
 * @param {unknown} v
 * @returns {import('./types').ExcludedRowCondition[]}
 */
export const _excludedRowConditionList = (v) =>
  _toArray(v)
    .map((e) => {
      if (!e || typeof e !== 'object') return null
      return {
        column: _string(e.column ?? e.Column),
        values: _exclusionValuesArray(e.values ?? e.Values)
      }
    })
    .filter(Boolean)

/**
 * GenericUploadFile(UploadFile) のレコードをFileStateに変換
 *
 * @param {any} item
 * @returns {import('./types').FileState}
 */
export function mapGenericUploadFileToFileState(item) {
  const get = (obj, ...keys) => {
    for (const key of keys) {
      if (obj && obj[key] !== undefined) return obj[key]
    }
    return undefined
  }

  const searchKey = _string(get(item, 'SearchKey', 'search_key'))
  const searchKeyParts = searchKey ? searchKey.split('#') : []

  const fileKey =
    _string(get(item, 'file_key', 'FileKey', 'FileId', 'file_id', 'Key', 'key')) ||
    _string(searchKeyParts[searchKeyParts.length - 1])

  const rawFileType = _string(get(item, 'FileType', 'file_type', 'file_kind'))
  const fileType =
    rawFileType === FILE_TYPE.CSV
      ? FILE_TYPE.CSV
      : [FILE_TYPE.EXCEL, 'xlsx', 'xls'].includes(rawFileType)
        ? FILE_TYPE.EXCEL
        : FILE_TYPE.CSV

  const rawStatus = _string(get(item, 'Status', 'file_status', 'fileState'))
  const fileState = Object.values(FILE_STATE).includes(rawStatus) ? rawStatus : FILE_STATE.PENDING_VIRUS_CHECK

  const headers = _stringArray(
    get(item, 'ColumnList', 'file_headers', 'Headers', 'headers', 'FileHeaders', 'fileHeaders')
  )

  const joinList = _toArray(item.join_column_list ?? item.JoinColumnList)
  const joinFirst = joinList[0] ?? {}

  const primaryKeyColumn = _string(
    get(item, 'PrimaryKeyCol', 'primary_key_col', 'PrimaryKeyColumn', 'primaryKeyColumn') ||
      joinFirst.primary_key ||
      joinFirst.PrimaryKey
  )

  const idConversionTable = _string(
    get(item, 'ConversionTableKey', 'conversion_table_key', 'idConversionTable') ||
      joinFirst.conversion_table_key ||
      joinFirst.ConversionTableKey
  )

  const exclusionConditions = _excludedRowConditionList(
    get(item, 'ExcludedRowConditionList', 'excluded_row_condition_list', 'exclusionConditions')
  )

  const hashColumns = _stringArray(get(item, 'HashColumnList', 'hash_column_list', 'hashColumns', 'hashColumnList'))

  const fileClassification = _string(
    get(item, 'FileClassification', 'file_classification', 'fileClassification', 'MappingFileType', 'mapping_file_type')
  )

  return {
    fileKey,
    fileName: _string(get(item, 'FileName', 'file_name', 'fileName')),
    fileSize: _number(get(item, 'FileSize', 'file_size', 'fileSize')),
    fileType,
    fileState,
    headers,
    rowCount: _number(get(item, 'RowCount', 'row_count', 'RecordCount', 'record_count', 'rowCount')),
    fileClassification,
    primaryKeyColumn,
    dataCategory: _string(get(item, 'DataCategory', 'data_category', 'dataCategory')),
    idConversionTable,
    exclusionConditions,
    registeredExclusionConditions: cloneExclusionConditions(exclusionConditions),
    hashColumns,
    registeredHashColumns: hashColumns,
    sheetName: _string(get(item, 'SheetName', 'sheet_name', 'sheetName')) || (fileType === 'csv' ? '0' : ''),
    sheetList: _stringArray(get(item, 'SheetList', 'sheet_list', 'sheetList')),
    uploadedUsername: _string(get(item, 'CreateUserName', 'upload_user_name', 'uploadedUsername')),
    uploadedDateTime: _string(
      get(item, 'FileUpdateDateTime', 'file_upload_date_time', 'CreateDateTime', 'uploadedDateTime', 'create_date_time')
    ),
    isStoredLocally: false,
    isUploadedLocally: false,
    isBaseFile: Boolean(get(item, 'IsBaseFile', 'is_base_file', 'isBaseFile')),
    isChanged: false,
    updateUserName: _string(get(item, 'UpdateUserName', 'update_user_name', 'updateUserName')),
    updatedAt: _string(get(item, 'UpdateDateTime', 'updatedAt', 'updated_at', 'update_date_time'))
  }
}

/**
 * ファイルをアップロード日時（uploadedDateTime）昇順に並べ替える純粋関数。
 *
 * 表示順をアップロード順に固定するために使用する。uploadedDateTime は登録時の create_date_time（アップロード日時・ミリ秒精度）に対応し、詳細設定の変更や
 * ファイル差し替えでは更新されないため、順序は安定する。日時が同値・パース不可の 場合は fileKey で決定的にタイブレークし、再読込しても順序が変わらないようにする。 日時は "YYYY/MM/DDThh:mm:ss.sssZ"
 * 等のスラッシュ形式でも比較できるよう、Date へ パースして数値比較する（パース不可は末尾へ送る）。
 *
 * @param {import('./types').FileState[]} files
 * @returns {import('./types').FileState[]}
 */
export const sortFilesByUploadOrder = (files) => {
  const toTime = (value) => {
    const time = new Date(_string(value).replace(/\//g, '-')).getTime()
    return Number.isNaN(time) ? Number.POSITIVE_INFINITY : time
  }
  return [...(files || [])].sort((a, b) => {
    // 減算だと Infinity - Infinity = NaN となり比較が壊れるため大小比較で判定する
    const timeA = toTime(a?.uploadedDateTime)
    const timeB = toTime(b?.uploadedDateTime)
    if (timeA < timeB) return -1
    if (timeA > timeB) return 1
    return _string(a?.fileKey).localeCompare(_string(b?.fileKey))
  })
}

/**
 * 変更があったファイルのキーを特定する（仕様 9.6: 新規/設定変更/ファイル変更/削除対象）
 *
 * @param {Record<string, import('./types').FileState>} savedUploadState - 保存済みの状態
 * @param {Record<string, import('./types').FileState>} fileList - 現在の状態
 * @returns {string[]} 変更があったファイルのキー配列
 */
export const detectChangedFileKeys = (savedUploadState, fileList) => {
  const savedState = savedUploadState || {}
  const currentFiles = fileList
  const changedFileKeys = new Set()

  Object.values(currentFiles).forEach((file) => {
    const savedFile = savedState[file.fileKey]

    // 1. 新規アップロード
    if (!savedFile && !isFileDeleted(file.fileState)) {
      changedFileKeys.add(file.fileKey)
      return
    }

    // 2. 削除対象
    if (isFileDeleted(file.fileState)) {
      changedFileKeys.add(file.fileKey)
      return
    }

    // 3. 変更フラグが立っている
    if (file.isChanged === true) {
      changedFileKeys.add(file.fileKey)
      return
    }

    if (!savedFile) return

    // 4. ファイル変更チェック（uploaded/virus-check-errorの場合）
    if (
      (file.fileState === FILE_STATE.UPLOADED || file.fileState === FILE_STATE.VIRUS_CHECK_ERROR) &&
      file.isUploadedLocally === false
    ) {
      if (file.fileName !== savedFile.fileName || !shallowEqualArrays(file.headers, savedFile.headers)) {
        changedFileKeys.add(file.fileKey)
        return
      }
    }

    // 5. 設定変更チェック
    const isConfigChanged =
      file.fileClassification !== savedFile.fileClassification ||
      file.primaryKeyColumn !== savedFile.primaryKeyColumn ||
      _string(file.dataCategory) !== _string(savedFile.dataCategory) ||
      file.idConversionTable !== savedFile.idConversionTable ||
      file.isBaseFile !== savedFile.isBaseFile ||
      !deepEqualExclusionConditions(file.exclusionConditions, savedFile.exclusionConditions) ||
      !shallowEqualArrays(file.hashColumns || [], savedFile.hashColumns || []) ||
      file.sheetName !== savedFile.sheetName ||
      file.fileName !== savedFile.fileName ||
      !shallowEqualArrays(file.headers, savedFile.headers)

    if (isConfigChanged) {
      changedFileKeys.add(file.fileKey)
    }
  })

  // 6. 削除されたファイル
  Object.keys(savedState).forEach((savedKey) => {
    if (!currentFiles[savedKey]) {
      changedFileKeys.add(savedKey)
    }
  })

  return Array.from(changedFileKeys)
}

/**
 * IF02104 の過去タスクファイル設定を、現在のファイルのヘッダーと照合して自動反映用の更新オブジェクトを生成する（仕様 9.9）。
 *
 * - データ種別は列照合の対象外でそのまま設定する
 * - キー項目・除外条件・ハッシュ化列は headers に「全く同名の列」が存在するもののみ設定し、同名列がないものは初期状態（未設定）とする
 * - 除外条件の values は現在ファイルのユニーク値と照合せずそのまま設定する（値選択UIで未存在値は表示されないため）
 * - ベースファイルフラグは月次タスクかつデータ種別が「従業員別」の場合のみ設定する（四半期・年次はトグル自体が存在しない）
 * - 従業員ID変換表はキー項目が設定でき、かつデータ種別が「従業員別」の場合のみ設定する（変換表はキー項目の従業員ID変換にのみ使用するため）
 *
 * @param {import('./types').FileState} file - ファイル種別を選択した対象ファイル
 * @param {import('./types').PreviousFileSettings} settings - IF02104 の過去設定（正規化済み）
 * @param {boolean} [isMonthlyTask] - タスクの期間種別が月次（monthly）かどうか
 * @returns {Partial<import('./types').FileState>} 対象ファイルへ適用する更新オブジェクト
 */
export const applyPreviousSettings = (file, settings, isMonthlyTask = false) => {
  const headers = Array.isArray(file?.headers) ? file.headers : []
  const headerSet = new Set(headers)

  const dataCategory = _string(settings?.dataCategory)
  const primaryKey = _string(settings?.primaryKey)
  const primaryKeyColumn = headerSet.has(primaryKey) ? primaryKey : ''
  const exclusionConditions = _toArray(settings?.excludedRowConditionList)
    .filter((cond) => headerSet.has(_string(cond?.column)))
    .map((cond) => ({ column: _string(cond.column), values: _exclusionValuesArray(cond.values) }))
  const hashColumns = _stringArray(settings?.hashColumnList).filter((column) => headerSet.has(column))
  const isBaseFile = isMonthlyTask && dataCategory === DATA_CATEGORY.EMPLOYEE && Boolean(settings?.isBaseFile)
  const idConversionTable =
    primaryKeyColumn !== '' && dataCategory === DATA_CATEGORY.EMPLOYEE ? _string(settings?.conversionTableKey) : ''

  return {
    dataCategory,
    primaryKeyColumn,
    idConversionTable,
    exclusionConditions,
    hashColumns,
    isBaseFile
  }
}

/**
 * ファイル種別を「未選択」に戻したときの更新値を返す。 種別に紐づく設定（キー項目・データ種別・ベースファイル・詳細設定）を残すと、非活性のまま値が残留し 登録時バリデーションや変更検知に影響するため、種別選択時に自動反映される
 * applyPreviousSettings と同じフィールド群をクリアする。
 *
 * @returns {Partial<import('./types').FileState>}
 */
export const buildUnsetClassificationUpdates = () => ({
  fileClassification: '',
  primaryKeyColumn: '',
  dataCategory: '',
  isBaseFile: false,
  idConversionTable: '',
  exclusionConditions: [],
  hashColumns: []
})

/**
 * キー項目変更時の更新値を返す。 従業員ID変換表はキー項目の従業員ID変換にのみ使用するため、キー項目を未選択に戻した場合はクリアする。
 * キー項目はハッシュ化対象列に指定できないため、選択列がハッシュ化対象列にチェック済みの場合はチェックを外す （画面はキー項目と同名のチェックボックスを非表示にするだけで hashColumns には残るため、ここで除去しないと
 * キー列がハッシュ化対象として登録され、かつデータ種別によってはハッシュ化されない不整合が生じる）。
 *
 * @param {string} newKeyField - 変更後のキー項目（未選択は空文字）
 * @param {string[]} [currentHashColumns] - 現在のハッシュ化対象列（未登録チェック分）
 * @returns {Partial<import('./types').FileState>}
 */
export const buildKeyFieldChangeUpdates = (newKeyField, currentHashColumns = []) => {
  const updates = { primaryKeyColumn: newKeyField }
  if (!newKeyField) {
    updates.idConversionTable = ''
  }
  if (newKeyField && (currentHashColumns || []).includes(newKeyField)) {
    updates.hashColumns = currentHashColumns.filter((column) => column !== newKeyField)
  }
  return updates
}

/**
 * データ種別変更時の更新値を返す。 「従業員別」以外へ変更した場合は、従業員別のみ有効な設定（ベースファイルフラグ・従業員ID変換表）をクリアする（仕様 画面状態⑨）。
 *
 * @param {string} newDataCategory - 変更後のデータ種別
 * @returns {Partial<import('./types').FileState>}
 */
export const buildDataCategoryChangeUpdates = (newDataCategory) => {
  const updates = { dataCategory: newDataCategory }
  if (newDataCategory !== DATA_CATEGORY.EMPLOYEE) {
    updates.isBaseFile = false
    updates.idConversionTable = ''
  }
  return updates
}
